import { ReactNode } from '@tanstack/react-router'
import _ from 'lodash'
import { createContext, FC, useContext, useMemo } from 'react'

import { CORE_SCHEMA } from './ontology/healthOntology'
import {
  deriveIdFields,
  DrilldownField,
  DrilldownFieldType,
  DrilldownRolodexType,
  DrilldownSchemaConfig,
  mergeSchemas,
  TableSchemaColumnDef,
} from './ontology/ontologyTypes'

/**
 * These are the fields you can currently filter on.
 * The format is 'type'.'field', where nested fields can be separated by more '.'.
 * No particular reason not to pull this out into config eventually.
 */

export type FieldDef = {
  name: string
  fieldType: DrilldownFieldType
  entryType: DrilldownRolodexType
  key: string
  keyOrField: 'key' | 'field'
  customColumns: ReadonlyArray<TableSchemaColumnDef>
  allowedValues: ReadonlyArray<string>
}

export type DrilldownDataSchemaContextType = {
  getInflectionType: (
    branchOne: ReadonlyArray<DrilldownRolodexType>,
    branchTwo: ReadonlyArray<DrilldownRolodexType>
  ) => DrilldownRolodexType | null
  bucketLinkedTypes: (
    types: ReadonlyArray<DrilldownRolodexType>
  ) => ReadonlyArray<ReadonlyArray<DrilldownRolodexType>>
  isDescendant: (
    type: DrilldownRolodexType,
    ancestor: DrilldownRolodexType
  ) => boolean
  findLowestType: (
    types: ReadonlyArray<DrilldownRolodexType>
  ) => DrilldownRolodexType | null
  fields: Record<DrilldownField, FieldDef>
  entryTypeNames: Readonly<Record<DrilldownRolodexType, string>>
}

function extractType(field: DrilldownField): DrilldownRolodexType {
  const dotIdx = field.indexOf('.')
  return field.substring(0, dotIdx)
}

function validateMergedSchema(config: DrilldownSchemaConfig) {
  const tagGraph = config.typeToRequiredTags
  validateTypeGraph(tagGraph)
  validateTransitiveClosure(tagGraph)

  const fields = Object.keys(config.fields)
  const graphTypes = new Set(Object.keys(tagGraph))
  fields.forEach((f) => {
    const fieldType = extractType(f)
    if (!graphTypes.has(fieldType)) {
      console.error(`Type ${fieldType} was missing from graph`)
    }
  })
}

export const DEFAULT_DATA_SCHEMA_CONTEXT: DrilldownDataSchemaContextType =
  createDataSchemaContext(CORE_SCHEMA)

function toTitleCase(input: string): string {
  // Replace hyphens and underscores with spaces
  const cleanedInput = input.replace(/[-_]/g, ' ')

  // Split the string into words, capitalize each word, and join them back together
  const titleCased = cleanedInput
    .split(' ')
    .map((word) => {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    })
    .join(' ')

  return titleCased
}

/**
 * Helper function to extract out the type and field from a FilterField (see above).
 * @param field the field
 * @returns a tuple of ['entry-type', 'key']
 */
function getTypeAndKey(field: DrilldownField): [string, string] {
  const split = field.split('.')
  return [split[0], field.substring(split[0].length + 1)]
}

function createDataSchemaContext(
  schema: DrilldownSchemaConfig
): DrilldownDataSchemaContextType {
  const tagGraph = schema.typeToRequiredTags
  const typeNames = schema.typeNames

  const fields: Record<DrilldownField, FieldDef> = Object.fromEntries(
    Object.entries(schema.fields).map(([key, value]) => {
      const [entryType, fieldKey] = getTypeAndKey(key)
      return [
        key,
        {
          name: value.name,
          fieldType: value.type,
          key: fieldKey,
          entryType,
          keyOrField:
            fieldKey.startsWith('id') ||
            fieldKey.startsWith('tags') ||
            fieldKey.startsWith('data')
              ? 'field'
              : 'key',
          customColumns: value.customColumns ?? [],
          allowedValues: value.allowedValues ?? [],
        },
      ]
    })
  )

  // validate
  validateMergedSchema(schema)

  return {
    getInflectionType: (branchOne, branchTwo) =>
      getInflectionType(branchOne, branchTwo, tagGraph),
    bucketLinkedTypes: (types) => bucketLinkedTypes(types, tagGraph),
    isDescendant: (type, ancestor) => isDescendant(type, ancestor, tagGraph),
    findLowestType: (types) => findLowestType(types, tagGraph),
    fields,
    entryTypeNames: Object.fromEntries(
      Object.keys(tagGraph).map((type) => [
        type,
        typeNames[type] ?? toTitleCase(type),
      ])
    ),
  }
}

function validateTypeGraph(
  typeGraph: Record<DrilldownRolodexType, ReadonlyArray<DrilldownRolodexType>>
) {
  const allTypes = new Set(
    Object.entries(typeGraph).flatMap(([type, requiredTags]) => [
      type as DrilldownRolodexType,
      ...(requiredTags as DrilldownRolodexType[]),
    ])
  )
  // Ensure every type is connected
  allTypes.forEach((from) => {
    allTypes.forEach((to) => {
      if (from === to) {
        return
      }
      if (getInflectionType([from], [to], typeGraph) === null) {
        console.error(
          `Config error in Drilldown - disconnected type! No route between [${from}] and [${to}]`
        )
      }
    })
  })
}

/**
 * Validate that, for every type in the graph, it contains
 * the tags of all of its ancestors. That is, we have transitivity
 * in the tags going upward: "If unit is tagged to site, and site
 * is tagged to organization, then unit is also tagged to organization".
 * @param tagGraph graph to verify
 */
function validateTransitiveClosure(
  tagGraph: Record<string, ReadonlyArray<string>>
) {
  const errors: string[] = []

  // Helper function to recursively collect all ancestors
  function collectAncestors(
    type: string,
    visited = new Set<string>()
  ): Set<string> {
    const directAncestors = tagGraph[type] || []
    for (const ancestor of directAncestors) {
      if (!visited.has(ancestor)) {
        visited.add(ancestor)
        collectAncestors(ancestor, visited)
      }
    }
    return visited
  }

  // Check each type
  for (const type of Object.keys(tagGraph)) {
    const expectedAncestors = collectAncestors(type)
    const actualAncestors = new Set(tagGraph[type])

    // Check if actual ancestors match expected ancestors
    for (const ancestor of expectedAncestors) {
      if (!actualAncestors.has(ancestor)) {
        errors.push(
          `Type "${type}" is missing ancestor "${ancestor}" in its transitive closure.`
        )
      }
    }

    // Check for extra ancestors that shouldn't be there
    for (const ancestor of actualAncestors) {
      if (!expectedAncestors.has(ancestor)) {
        errors.push(
          `Type "${type}" incorrectly includes "${ancestor}" as an ancestor.`
        )
      }
    }
  }
  errors.forEach((err) => console.error(`Drilldown config error: ${err}`))
}

const DrilldownDataSchemaContext =
  createContext<DrilldownDataSchemaContextType>(DEFAULT_DATA_SCHEMA_CONTEXT)

function getInflectionType(
  branchOne: ReadonlyArray<DrilldownRolodexType>,
  branchTwo: ReadonlyArray<DrilldownRolodexType>,
  tagGraph: Record<DrilldownRolodexType, ReadonlyArray<DrilldownRolodexType>>
): DrilldownRolodexType | null {
  const commonAncestors = Object.keys(tagGraph)
    .map((s) => s as DrilldownRolodexType)
    .filter(
      (type) =>
        branchOne.some((filterType) =>
          isDescendant(filterType, type, tagGraph)
        ) &&
        branchTwo.some((filterType) => isDescendant(filterType, type, tagGraph))
    )
  return findLowestType(commonAncestors, tagGraph)
}

export function useDrilldownSchema(): DrilldownDataSchemaContextType {
  return useContext(DrilldownDataSchemaContext)
}

function bucketLinkedTypes(
  types: ReadonlyArray<DrilldownRolodexType>,
  tagGraph: Record<DrilldownRolodexType, ReadonlyArray<DrilldownRolodexType>>
): ReadonlyArray<ReadonlyArray<DrilldownRolodexType>> {
  const buckets: DrilldownRolodexType[][] = []
  types.forEach((t) => {
    const matchingBucket = buckets.find((bucket) =>
      bucket.every((item) => {
        return (
          isDescendant(item, t, tagGraph) || isDescendant(t, item, tagGraph)
        )
      })
    )
    if (matchingBucket) {
      matchingBucket.push(t)
    } else {
      buckets.push([t])
    }
  })
  return buckets
}

/**
 * Determine whether a type is a descendant of some other type (or is the same type).
 *
 * @param type to check whether it is a descendant
 * @param ancestor to check whenther type is a descendant of
 * @returns true, if 'type' is a descendant of 'ancestor', or equal, false otherwise
 */
function isDescendant(
  type: DrilldownRolodexType,
  ancestor: DrilldownRolodexType,
  tagGraph: Record<DrilldownRolodexType, ReadonlyArray<DrilldownRolodexType>>
): boolean {
  if (type === ancestor) return true
  const requiredTags = tagGraph[type]
  return requiredTags?.includes(ancestor)
}

function findLowestType(
  types: ReadonlyArray<DrilldownRolodexType>,
  tagGraph: Record<DrilldownRolodexType, ReadonlyArray<DrilldownRolodexType>>
): DrilldownRolodexType | null {
  const candidates = findAllLowestTypes(types, tagGraph)
  return _.first(candidates.toSorted()) ?? null
}

function findAllLowestTypes(
  types: ReadonlyArray<DrilldownRolodexType>,
  tagGraph: Record<DrilldownRolodexType, ReadonlyArray<DrilldownRolodexType>>
): DrilldownRolodexType[] {
  return types.filter((candidate) =>
    types.every(
      (otherType) =>
        candidate === otherType || !isDescendant(otherType, candidate, tagGraph)
    )
  )
}

export const DrilldownDataSchemaProvider: FC<{
  schemaAdditions: ReadonlyArray<DrilldownSchemaConfig> | undefined
  children: ReactNode
}> = ({ schemaAdditions, children }) => {
  const mergedSchemaConfig = useMemo(
    () => mergeSchemas(CORE_SCHEMA, ...(schemaAdditions ?? [])),

    [schemaAdditions]
  )

  const idFields = useMemo(
    () =>
      deriveIdFields(
        Object.keys(mergedSchemaConfig.typeNames),
        mergedSchemaConfig.typeNames
      ),
    [mergedSchemaConfig.typeNames]
  )

  const contextValue = useMemo(
    () =>
      createDataSchemaContext(
        mergeSchemas(
          { fields: idFields, typeNames: {}, typeToRequiredTags: {} },
          mergedSchemaConfig
        )
      ),
    [idFields, mergedSchemaConfig]
  )
  return (
    <DrilldownDataSchemaContext.Provider value={contextValue}>
      {children}
    </DrilldownDataSchemaContext.Provider>
  )
}
